import { OktaAuth } from '@okta/okta-auth-js'
import { OktaUser, OktaAuthnResponse, extractOktaOrgDomain } from '@hooks/useOkta'
import { configs } from '@configs'
import { createLogger } from '@utils/Logger'

export interface TokenProcessResult {
  success: boolean
  user?: OktaUser
  stateToken?: string
  sessionToken?: string
  error?: string
  isActivation?: boolean
  isPasswordReset?: boolean
}

export class AuthTokenProcessor {
  private static instance: AuthTokenProcessor

  private static logger = createLogger('AuthTokenProcessor')

  public static getInstance(): AuthTokenProcessor {
    if (!AuthTokenProcessor.instance) {
      AuthTokenProcessor.instance = new AuthTokenProcessor()
    }
    return AuthTokenProcessor.instance
  }

  /**
   * Process activation token using Okta SDK or direct API call
   * @param token - The activation or recovery token
   * @param isActivation - Whether this is an activation token (true) or recovery token (false)
   * @param oktaAuth - Optional OktaAuth instance for SDK-based processing
   * @returns Promise<TokenProcessResult>
   */
  public static async processToken(token: string, isActivation: boolean = true, oktaAuth?: OktaAuth): Promise<TokenProcessResult> {
    AuthTokenProcessor.logger.debug('Processing token', {
      token: `${token.substring(0, 10)}...`,
      isActivation,
      hasOktaAuth: !!oktaAuth,
    })

    if (!token) {
      return {
        success: false,
        error: 'No token provided',
      }
    }

    try {
      // Use Okta SDK if available, fallback to direct API call
      if (oktaAuth) {
        return await this.processWithSDK(token, isActivation, oktaAuth)
      }
      return await this.processWithDirectAPI(token, isActivation)
    } catch (error) {
      AuthTokenProcessor.logger.error('Token processing failed', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Token processing failed',
      }
    }
  }

  /**
   * Process token using Okta SDK
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private static async processWithSDK(token: string, isActivation: boolean, _oktaAuth: OktaAuth): Promise<TokenProcessResult> {
    try {
      AuthTokenProcessor.logger.debug('Using Okta SDK for token processing')

      // For activation tokens, we need to use the authn API directly
      // The SDK doesn't have a direct method for activation tokens
      return await this.processWithDirectAPI(token, isActivation)
    } catch (error) {
      AuthTokenProcessor.logger.error('SDK-based token processing failed', error)
      throw error
    }
  }

  /**
   * Process token using direct API call (current implementation)
   */
  private static async processWithDirectAPI(token: string, isActivation: boolean): Promise<TokenProcessResult> {
    // Validate configuration
    if (!configs.oidc.apiToken) {
      throw new Error('Okta API token is not configured')
    }

    if (!configs.oidc.issuer) {
      throw new Error('Okta issuer is not configured')
    }

    // Extract the org domain for authn calls
    const orgDomain = extractOktaOrgDomain(configs.oidc.issuer)
    const authnUrl = `${serverUrl}${API}/okta/authn`
    const payload = isActivation ? { token } : { recoveryToken: token }

    AuthTokenProcessor.logger.debug(`Making request to: ${authnUrl}`)

    const response = await fetch(authnUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      const errorText = await response.text()
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`

      try {
        const errorJson = JSON.parse(errorText)
        errorMessage = errorJson.errorSummary || errorJson.errorCode || errorMessage
        AuthTokenProcessor.logger.error('API Error', {
          status: response.status,
          statusText: response.statusText,
          errorSummary: errorJson.errorSummary,
          errorCode: errorJson.errorCode,
          errorCauses: errorJson.errorCauses,
        })
      } catch {
        AuthTokenProcessor.logger.error('Failed to parse error response', errorText)
      }

      return {
        success: false,
        error: errorMessage,
      }
    }

    const authnResponse: OktaAuthnResponse = await response.json()

    AuthTokenProcessor.logger.debug('Authn response received', {
      status: authnResponse.status,
      hasUser: !!authnResponse.user,
      hasStateToken: !!authnResponse.stateToken,
      hasSessionToken: !!authnResponse.sessionToken,
    })

    // Validate response status
    if (authnResponse.status !== 'SUCCESS' && authnResponse.status !== 'PASSWORD_RESET') {
      const errorMsg = authnResponse.errorSummary || `Invalid token status: ${authnResponse.status}`
      AuthTokenProcessor.logger.error('Invalid response status', {
        status: authnResponse.status,
        errorSummary: authnResponse.errorSummary,
        errorCode: authnResponse.errorCode,
      })

      return {
        success: false,
        error: errorMsg,
      }
    }

    // Extract user information
    const user = this.extractUserFromResponse(authnResponse)

    if (!user) {
      AuthTokenProcessor.logger.error('No user information found in response', authnResponse)
      return {
        success: false,
        error: 'Unable to extract user information from token',
      }
    }

    // Validate user information
    if (!this.validateUser(user)) {
      AuthTokenProcessor.logger.error('Invalid user information', user)
      return {
        success: false,
        error: 'User information is incomplete or invalid',
      }
    }

    AuthTokenProcessor.logger.info('Successfully processed token', {
      userLogin: user.login,
      userEmail: user.email,
      displayName: this.getUserDisplayName(user),
    })

    return {
      success: true,
      user,
      stateToken: authnResponse.stateToken,
      sessionToken: authnResponse.sessionToken,
      isActivation,
      isPasswordReset: authnResponse.status === 'PASSWORD_RESET',
    }
  }

  /**
   * Extract user information from Okta authn response
   */
  public static extractUserFromResponse(response: OktaAuthnResponse): OktaUser | null {
    // Try to extract user from various possible locations in the response
    if (response.user) {
      return this.normalizeUser(response.user)
    }

    // Check for embedded user data
    interface OktaAuthnResponseWithEmbedded extends OktaAuthnResponse {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      _embedded?: { user?: any }
    }
    // eslint-disable-next-line @typescript-eslint/dot-notation
    const embedded = (response as OktaAuthnResponseWithEmbedded)['_embedded']
    if (embedded?.user) {
      return this.normalizeUser(embedded.user)
    }

    return null
  }

  /**
   * Normalize user data to ensure consistent structure
   */
  public static normalizeUser(user: any): OktaUser {
    return {
      login: user.login || user.profile?.login || user.email || '',
      email: user.email || user.profile?.email || user.profile?.login || user.login || '',
      id: user.id || '',
      profile: {
        firstName: user.profile?.firstName || user.firstName || '',
        lastName: user.profile?.lastName || user.lastName || '',
        displayName: user.profile?.displayName || user.displayName || '',
        email: user.profile?.email || user.email || user.profile?.login || user.login || '',
      },
    }
  }

  /**
   * Validate that the parsed user has required information
   */
  public static validateUser(user: OktaUser): boolean {
    return !!(user.login || user.email)
  }

  /**
   * Get user display name for UI
   */
  public static getUserDisplayName(user: OktaUser): string {
    if (user.profile?.displayName) {
      return user.profile.displayName
    }

    if (user.profile?.firstName && user.profile?.lastName) {
      return `${user.profile.firstName} ${user.profile.lastName}`
    }

    if (user.profile?.firstName) {
      return user.profile.firstName
    }

    return user.login || user.email || 'User'
  }
}

// Export singleton instance
export const authTokenProcessor = AuthTokenProcessor.getInstance()
